/**
 * Menerjemahkan error axios menjadi pesan berbahasa Indonesia yang bisa
 * dimengerti user.
 *
 * Latar belakang: banyak komponen menampilkan
 *   text: `${err?.response?.data?.message}`
 * sehingga ketika server membalas HTML (error nginx / halaman error Express),
 * atau ketika request gagal sebelum sampai ke server (koneksi putus, timeout,
 * upload terlalu besar), user hanya melihat tulisan "undefined".
 */

const DEFAULT_MESSAGE =
  'Terjadi kesalahan yang tidak diketahui. Silakan coba lagi beberapa saat lagi.';

const STATUS_MESSAGES = {
  400: 'Data yang dikirim tidak lengkap atau tidak valid. Silakan periksa kembali isian Anda.',
  401: 'Sesi Anda sudah berakhir. Silakan login kembali.',
  403: 'Anda tidak memiliki akses untuk melakukan tindakan ini.',
  404: 'Data atau alamat layanan tidak ditemukan di server.',
  408: 'Permintaan terlalu lama diproses (timeout). Silakan coba lagi.',
  409: 'Data sedang diproses atau sudah ada. Silakan muat ulang halaman.',
  413: 'Ukuran file terlalu besar untuk diterima server. Silakan kompres file atau gunakan file yang lebih kecil.',
  415: 'Tipe file tidak didukung. Dokumen hanya boleh berformat PDF atau Excel.',
  422: 'Data yang dikirim tidak dapat diproses oleh server.',
  429: 'Terlalu banyak permintaan dalam waktu singkat. Silakan tunggu sebentar lalu coba lagi.',
  500: 'Server mengalami gangguan saat memproses permintaan. Silakan coba lagi beberapa saat lagi.',
  502: 'Server tidak memberikan balasan yang benar (bad gateway). Bila Anda sedang mengunggah file besar, coba dengan file yang lebih kecil.',
  503: 'Layanan sedang tidak tersedia atau sedang sibuk. Silakan coba lagi beberapa saat lagi.',
  504: 'Server terlalu lama merespons (gateway timeout). Bila Anda sedang mengunggah file besar, coba dengan file yang lebih kecil.',
  507: 'Penyimpanan server penuh sehingga data tidak dapat disimpan. Silakan hubungi administrator.',
};

const NETWORK_MESSAGES = {
  ECONNABORTED:
    'Koneksi terputus karena proses terlalu lama. Bila Anda mengunggah file besar, periksa koneksi internet lalu coba lagi.',
  ETIMEDOUT:
    'Koneksi ke server habis waktu (timeout). Silakan periksa koneksi internet Anda lalu coba lagi.',
  ERR_NETWORK:
    'Tidak dapat terhubung ke server. Periksa koneksi internet Anda, atau server sedang tidak dapat dijangkau.',
  ERR_CANCELED: 'Permintaan dibatalkan sebelum selesai.',
  ERR_BAD_RESPONSE:
    'Balasan dari server tidak dapat dibaca. Silakan coba lagi beberapa saat lagi.',
};

const isUsableText = (value) => {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  if (!text) return false;
  if (text === 'undefined' || text === 'null' || text === '[object Object]') return false;
  // Balasan berupa halaman HTML (error nginx / Express) bukan pesan untuk user.
  if (/^\s*<(!doctype|html|head|body)/i.test(text)) return false;
  return true;
};

/** Mengambil pesan dari body balasan server, bila ada dan layak ditampilkan. */
const readServerMessage = (data) => {
  if (!data) return null;
  if (isUsableText(data)) return data.trim();
  if (typeof data !== 'object') return null;

  const candidates = [
    data.message,
    data.error?.message,
    typeof data.error === 'string' ? data.error : null,
    Array.isArray(data.errors) ? data.errors.map((e) => e?.message || e).filter(Boolean).join(', ') : null,
    data.msg,
  ];

  return candidates.find(isUsableText)?.trim() || null;
};

/** Kode error dari backend, mis. STORAGE_FULL / AUTH_INVALID_SESSION. */
export const getApiErrorCode = (error) =>
  error?.response?.data?.code || error?.code || null;

export const getApiErrorStatus = (error) => error?.response?.status ?? null;

/** Sesi benar-benar habis (bukan sekadar server terganggu). */
export const isSessionExpiredError = (error) =>
  error?.response?.status === 401;

/** Gangguan sisi server/jaringan — sesi user tetap valid, jangan logout. */
export const isServerSideOutage = (error) => {
  const status = getApiErrorStatus(error);
  if (status === null) return true; // tidak ada balasan sama sekali
  return status >= 500;
};

export const getApiErrorMessage = (error, fallback = DEFAULT_MESSAGE) => {
  if (!error) return fallback;

  const response = error.response;

  if (response) {
    const serverMessage = readServerMessage(response.data);
    if (serverMessage) return serverMessage;
    return STATUS_MESSAGES[response.status] || `${fallback} (kode ${response.status})`;
  }

  // Request terkirim tapi tidak ada balasan sama sekali, atau gagal sebelum terkirim.
  const networkMessage = NETWORK_MESSAGES[error.code];
  if (networkMessage) return networkMessage;

  if (error.request) {
    return 'Server tidak memberikan balasan. Periksa koneksi internet Anda lalu coba lagi.';
  }

  return isUsableText(error.message) ? error.message : fallback;
};

export default getApiErrorMessage;
